import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Loader2, Plus, Trash2, X } from 'lucide-react';
import Aide from './Aide';
import { AIDES_ADHERENTS } from '../utils/aides';
import { fetchConfigurationAdherents, updateConfigurationAdherents } from '../services/api';
import SaisonsClub from './SaisonsClub';

type Categorie = { code?: string; libelle: string; ageMin: number; ageMax: number };
type Document = { code?: string; libelle: string; obligatoire: boolean; validiteMois: number };
type Precision = { code?: string; libelle: string; type: 'texte' | 'liste'; valeurs: string[] };
type Equipement = { code?: string; libelle: string; tailles: string[] };

type Config = {
  disciplines: string[];
  categories: Categorie[];
  typesLicence: string[];
  /**
   * La section handisport : fermée par défaut.
   *
   * Un club qui n'en a pas ne doit jamais voir le champ sur ses licences.
   * L'affiliation n'est pas la même — FFH pour les handicaps moteurs,
   * visuels et auditifs, FFSA pour le handicap mental et psychique — et le
   * numéro s'écrit sur les demandes de subvention.
   */
  handisport?: { actif: boolean; federation: string; numeroAffiliation: string };
  documents: Document[];
  precisions: Precision[];
  equipements: Equipement[];
  rolesEquipe: string[];
  relances: { jours: number[]; actives: boolean };
  vitrine: {
    equipes: boolean;
    nomsComplets: boolean;
    photos: boolean;
    photosMineurs: boolean;
    titre: string;
    texte: string;
  };
  prelevement: { actif: boolean; echeances: number[]; jour: number };
  fraisStripe: { reportes: boolean };
};

/**
 * Le barème de notre prestataire de paiement, pour montrer le calcul.
 *
 * Écrit ici en toutes lettres plutôt que demandé au serveur : c'est un exemple
 * chiffré, pas une donnée. Un réglage qui explique ce qu'il fait avec un
 * exemple concret se comprend en une lecture ; le même réglage décrit en
 * abstrait — « majore le montant des frais bancaires » — demande d'ouvrir une
 * calculette pour savoir si l'on est d'accord.
 */
const BAREME = { taux: 0.015, fixe: 25 };

/** (voulu + fixe) / (1 − taux), arrondi vers le haut, en centimes. */
function majorer(centimes: number): number {
  return Math.ceil((centimes + BAREME.fixe) / (1 - BAREME.taux));
}

const euros = (centimes: number) =>
  (centimes / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

/** Les étalements qu'un club peut ouvrir. 12 se dit « tous les mois ». */
const ETALEMENTS: { nombre: number; libelle: string; aide: string }[] = [
  { nombre: 3, libelle: 'En 3 fois', aide: 'Le trimestre : le plus demandé' },
  { nombre: 5, libelle: 'En 5 fois', aide: 'De septembre à janvier' },
  { nombre: 12, libelle: 'Tous les mois', aide: 'Douze prélèvements, douze occasions de rejet' },
];

/**
 * Une liste de mots qui s'ajoutent et se retirent, sous forme de pastilles.
 *
 * Disciplines, types de licence, rôles d'équipe, tailles : le club en déclare
 * autant qu'il veut, et c'est toujours le même geste.
 */
function ListeTexte({
  valeurs,
  onChange,
  placeholder,
}: {
  valeurs: string[];
  onChange: (valeurs: string[]) => void;
  placeholder: string;
}) {
  const [saisie, setSaisie] = useState('');

  const ajouter = () => {
    const mot = saisie.trim();
    if (!mot || valeurs.includes(mot)) return;
    onChange([...valeurs, mot]);
    setSaisie('');
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {valeurs.map((valeur) => (
          <span
            key={valeur}
            className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700"
          >
            {valeur}
            <button
              onClick={() => onChange(valeurs.filter((v) => v !== valeur))}
              className="text-gray-400 hover:text-red-600"
              title="Retirer"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </span>
        ))}
        {valeurs.length === 0 && <span className="text-sm text-gray-400">Liste vide.</span>}
      </div>
      <div className="flex gap-2">
        <input
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              ajouter();
            }
          }}
          placeholder={placeholder}
          className="flex-1 px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
        />
        <button
          onClick={ajouter}
          className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * Un bloc de réglage, replié tant qu'on ne le demande pas.
 *
 * Dépliés, les huit blocs font trois écrans de listes : les catégories d'âge,
 * les pièces réclamées, les équipements et leurs tailles. On vient ici pour
 * régler une chose, et il fallait défiler longtemps pour la trouver.
 *
 * Chacun garde son état — on compare parfois deux réglages — et le compteur
 * du titre dit combien de lignes s'y trouvent, ce qui évite d'ouvrir pour
 * voir.
 */
function Bloc({
  titre,
  aide,
  compte,
  ouvertParDefaut = false,
  children,
}: {
  titre: string;
  aide: string;
  compte?: number;
  ouvertParDefaut?: boolean;
  children: React.ReactNode;
}) {
  const [ouvert, setOuvert] = useState(ouvertParDefaut);

  return (
    <div className="border-t border-gray-100 pt-4">
      {/* Tout l'en-tête bascule le bloc, pas seulement le chevron : le titre
          est ce que l'œil a déjà trouvé. */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOuvert((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOuvert((o) => !o);
          }
        }}
        className="w-full flex items-start justify-between gap-3 text-left cursor-pointer group"
      >
        <div className="min-w-0 flex flex-wrap items-center gap-x-2">
          <h4 className="text-sm font-semibold text-gray-900 group-hover:text-primary-700">
            {titre}
            {compte !== undefined && (
              <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-normal text-gray-600">
                {compte}
              </span>
            )}
          </h4>
          {/* Le « i » dit à quoi sert ce réglage, et ce qu'il change ailleurs
              dans le logiciel — une catégorie d'âge se retrouve sur les
              licences, les équipes et les feuilles de match. */}
          {AIDES_ADHERENTS[titre] && (
            <Aide titre={titre}>{AIDES_ADHERENTS[titre]}</Aide>
          )}
          {!ouvert && <p className="basis-full text-xs text-gray-500 mt-0.5">{aide}</p>}
        </div>
        {ouvert ? (
          <ChevronUp className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
        )}
      </div>

      {ouvert && (
        <div className="mt-3">
          <p className="text-xs text-gray-500 mb-3">{aide}</p>
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Le bloc « Gestion des adhérents » des Paramètres.
 *
 * Un club de judo classe par ceinture, un club de foot par poste, un club de
 * danse par cours : coder ces listes reviendrait à écrire un logiciel par
 * sport. Elles se règlent donc ici, et le registre s'y plie sans développeur.
 *
 * La configuration s'édite entière puis s'enregistre d'un bloc : elle est
 * courte, et un envoi complet évite les demi-listes quand deux administrateurs
 * la touchent en même temps.
 */
export default function GestionAdherents() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['adherents-config'], queryFn: fetchConfigurationAdherents });

  const [config, setConfig] = useState<Config | null>(null);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (config === null && query.data?.configuration) {
      setConfig(JSON.parse(JSON.stringify(query.data.configuration)));
    }
  }, [query.data, config]);

  const enregistrer = useMutation({
    mutationFn: (valeur: Config) => updateConfigurationAdherents(valeur),
    onSuccess: (d: any) => {
      setNotice({ type: 'success', message: d.message });
      setConfig(JSON.parse(JSON.stringify(d.configuration)));
      queryClient.invalidateQueries({ queryKey: ['adherents-config'] });
      queryClient.invalidateQueries({ queryKey: ['adherents'] });
      queryClient.invalidateQueries({ queryKey: ['equipes-publiques'] });
      setTimeout(() => setNotice(null), 2500);
    },
    onError: (e: Error) => setNotice({ type: 'error', message: e.message }),
  });

  if (query.isPending || config === null) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const modifier = (patch: Partial<Config>) => setConfig({ ...config, ...patch });

  const modifierLigne = <T,>(liste: T[], index: number, patch: Partial<T>): T[] =>
    liste.map((ligne, i) => (i === index ? { ...ligne, ...patch } : ligne));

  return (
    <div className="space-y-5">
      {notice && (
        <div
          className={`rounded-lg px-4 py-2 text-sm ${
            notice.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {notice.message}
        </div>
      )}

      {/* Les saisons ouvertes d'emblée : c'est le premier geste d'un club qui
          arrive ici en août, et le seul qui bloque tout le reste. */}
      <Bloc
        titre="Saisons"
        aide="Une saison sportive court de l'été à l'été : c'est elle qui découpe le registre."
        ouvertParDefaut
      >
        <SaisonsClub />
      </Bloc>

      <Bloc
        titre="Disciplines"
        aide="Une seule pour un club mono-sport, autant que de sections pour un omnisports."
        compte={config.disciplines.length}
      >
        <ListeTexte
          valeurs={config.disciplines}
          onChange={(disciplines) => modifier({ disciplines })}
          placeholder="Football, Judo, Danse…"
        />
      </Bloc>

      <Bloc
        titre="Catégories d'âge"
        compte={config.categories.length}
        aide="Les bornes sont incluses et se comparent à l'âge révolu : la catégorie d'un adhérent se calcule seule."
      >
        <div className="space-y-2">
          {config.categories.map((categorie, index) => (
            <div key={index} className="flex flex-wrap items-center gap-2">
              <input
                value={categorie.libelle}
                onChange={(e) => modifier({ categories: modifierLigne(config.categories, index, { libelle: e.target.value }) })}
                placeholder="Libellé"
                className="flex-1 min-w-[8rem] px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
              />
              <input
                type="number"
                min={0}
                max={120}
                value={categorie.ageMin}
                onChange={(e) => modifier({ categories: modifierLigne(config.categories, index, { ageMin: Number(e.target.value) }) })}
                className="w-20 px-2 py-1.5 rounded-lg border border-gray-300 text-sm"
              />
              <span className="text-xs text-gray-400">à</span>
              <input
                type="number"
                min={0}
                max={120}
                value={categorie.ageMax}
                onChange={(e) => modifier({ categories: modifierLigne(config.categories, index, { ageMax: Number(e.target.value) }) })}
                className="w-20 px-2 py-1.5 rounded-lg border border-gray-300 text-sm"
              />
              <span className="text-xs text-gray-400">ans</span>
              <button
                onClick={() => modifier({ categories: config.categories.filter((_, i) => i !== index) })}
                className="p-1.5 rounded text-red-600 hover:bg-red-50"
                title="Retirer la catégorie"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={() => modifier({ categories: [...config.categories, { libelle: '', ageMin: 0, ageMax: 120 }] })}
          className="mt-2 inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800"
        >
          <Plus className="w-4 h-4" /> Ajouter une catégorie
        </button>
      </Bloc>

      <Bloc titre="Types de licence"
        compte={config.typesLicence.length} aide="Ce que la fédération distingue : compétition, loisir, dirigeant…">
        <ListeTexte
          valeurs={config.typesLicence}
          onChange={(typesLicence) => modifier({ typesLicence })}
          placeholder="Compétition, Loisir…"
        />
      </Bloc>

      {/*
        LA SECTION HANDISPORT.

        Fermée par défaut, et c'est le point : un club qui n'en a pas ne doit
        jamais voir la question sur ses licences. La poser à tout le monde
        ferait répondre « valide » cinq cents fois pour rien — et une case
        qu'on coche sans y penser finit par être cochée de travers.

        Deux fédérations et non une : la FFH couvre les handicaps moteurs,
        visuels et auditifs ; la FFSA le handicap mental et psychique. Ce ne
        sont ni les mêmes catégories ni les mêmes compétitions, et un club
        affilié à l'une ne l'est pas à l'autre. Le numéro s'écrit sur les
        demandes de subvention et sur les engagements.
      */}
      <Bloc
        titre="Section handisport"
        aide="Ouvre le choix « valide / handisport » sur chaque licence. L'affiliation est distincte de celle de la discipline : FFH pour les handicaps moteurs, visuels et auditifs, FFSA pour le handicap mental et psychique."
      >
        <label className="flex items-start gap-2.5 text-sm">
          <input
            type="checkbox"
            checked={config.handisport?.actif ?? false}
            onChange={(e) =>
              modifier({
                handisport: {
                  federation: config.handisport?.federation ?? '',
                  numeroAffiliation: config.handisport?.numeroAffiliation ?? '',
                  actif: e.target.checked,
                },
              })
            }
            className="mt-0.5 w-4 h-4 shrink-0"
          />
          <span>
            Le club accueille une section handisport
            <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
              Chaque licence porte alors sa pratique. Les licences déjà enregistrées ne sont pas
              touchées : elles restent sans mention, ce qui se lit comme la pratique ordinaire.
            </span>
          </span>
        </label>

        {config.handisport?.actif ? (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs text-gray-500 dark:text-gray-400">
              Fédération
              <select
                value={config.handisport?.federation ?? ''}
                onChange={(e) =>
                  modifier({
                    handisport: {
                      actif: true,
                      numeroAffiliation: config.handisport?.numeroAffiliation ?? '',
                      federation: e.target.value,
                    },
                  })
                }
                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm"
              >
                <option value="">À préciser…</option>
                <option value="FFH">FFH — Handisport (moteur, visuel, auditif)</option>
                <option value="FFSA">FFSA — Sport adapté (mental, psychique)</option>
                <option value="FFH+FFSA">Les deux</option>
              </select>
            </label>
            <label className="text-xs text-gray-500 dark:text-gray-400">
              Numéro d'affiliation
              <input
                value={config.handisport?.numeroAffiliation ?? ''}
                onChange={(e) =>
                  modifier({
                    handisport: {
                      actif: true,
                      federation: config.handisport?.federation ?? '',
                      numeroAffiliation: e.target.value,
                    },
                  })
                }
                placeholder="Celui de la section, distinct de la discipline"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm"
              />
            </label>
          </div>
        ) : null}
      </Bloc>

      <Bloc
        titre="Pièces réclamées"
        compte={config.documents.length}
        aide="« Obligatoire » nourrit l'alerte du registre ; la validité en mois propose une date de péremption au dépôt (0 = sans limite)."
      >
        <div className="space-y-2">
          {config.documents.map((document, index) => (
            <div key={index} className="flex flex-wrap items-center gap-2">
              <input
                value={document.libelle}
                onChange={(e) => modifier({ documents: modifierLigne(config.documents, index, { libelle: e.target.value }) })}
                placeholder="Certificat médical…"
                className="flex-1 min-w-[10rem] px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
              />
              <label className="flex items-center gap-1.5 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={document.obligatoire}
                  onChange={(e) => modifier({ documents: modifierLigne(config.documents, index, { obligatoire: e.target.checked }) })}
                  className="rounded border-gray-300 text-primary-600"
                />
                Obligatoire
              </label>
              <input
                type="number"
                min={0}
                max={120}
                value={document.validiteMois}
                onChange={(e) => modifier({ documents: modifierLigne(config.documents, index, { validiteMois: Number(e.target.value) }) })}
                className="w-20 px-2 py-1.5 rounded-lg border border-gray-300 text-sm"
                title="Validité en mois"
              />
              <span className="text-xs text-gray-400">mois</span>
              <button
                onClick={() => modifier({ documents: config.documents.filter((_, i) => i !== index) })}
                className="p-1.5 rounded text-red-600 hover:bg-red-50"
                title="Retirer la pièce"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={() =>
            modifier({ documents: [...config.documents, { libelle: '', obligatoire: false, validiteMois: 0 }] })
          }
          className="mt-2 inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800"
        >
          <Plus className="w-4 h-4" /> Ajouter une pièce
        </button>
      </Bloc>

      <Bloc
        titre="Précisions propres au sport"
        compte={config.precisions.length}
        aide="Ce que la fiche demande en plus : un poste au football, une ceinture au judo, un cours à la danse."
      >
        <div className="space-y-3">
          {config.precisions.map((precision, index) => (
            <div key={index} className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <input
                  value={precision.libelle}
                  onChange={(e) => modifier({ precisions: modifierLigne(config.precisions, index, { libelle: e.target.value }) })}
                  placeholder="Poste, Ceinture, Cours…"
                  className="flex-1 min-w-[10rem] px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
                />
                <select
                  value={precision.type}
                  onChange={(e) =>
                    modifier({
                      precisions: modifierLigne(config.precisions, index, {
                        type: e.target.value as 'texte' | 'liste',
                      }),
                    })
                  }
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
                >
                  <option value="texte">Saisie libre</option>
                  <option value="liste">Choix dans une liste</option>
                </select>
                <button
                  onClick={() => modifier({ precisions: config.precisions.filter((_, i) => i !== index) })}
                  className="p-1.5 rounded text-red-600 hover:bg-red-50"
                  title="Retirer la précision"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {precision.type === 'liste' && (
                <ListeTexte
                  valeurs={precision.valeurs}
                  onChange={(valeurs) => modifier({ precisions: modifierLigne(config.precisions, index, { valeurs }) })}
                  placeholder="Une valeur possible"
                />
              )}
            </div>
          ))}
        </div>
        <button
          onClick={() =>
            modifier({ precisions: [...config.precisions, { libelle: '', type: 'texte', valeurs: [] }] })
          }
          className="mt-2 inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800"
        >
          <Plus className="w-4 h-4" /> Ajouter une précision
        </button>
      </Bloc>

      <Bloc titre="Équipements"
        compte={config.equipements.length} aide="Ce que le club fournit, prête ou vend, avec les tailles disponibles.">
        <div className="space-y-3">
          {config.equipements.map((equipement, index) => (
            <div key={index} className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <input
                  value={equipement.libelle}
                  onChange={(e) => modifier({ equipements: modifierLigne(config.equipements, index, { libelle: e.target.value }) })}
                  placeholder="Maillot, Short…"
                  className="flex-1 px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
                />
                <button
                  onClick={() => modifier({ equipements: config.equipements.filter((_, i) => i !== index) })}
                  className="p-1.5 rounded text-red-600 hover:bg-red-50"
                  title="Retirer l'équipement"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <ListeTexte
                valeurs={equipement.tailles}
                onChange={(tailles) => modifier({ equipements: modifierLigne(config.equipements, index, { tailles }) })}
                placeholder="Une taille"
              />
            </div>
          ))}
        </div>
        <button
          onClick={() => modifier({ equipements: [...config.equipements, { libelle: '', tailles: ['Taille unique'] }] })}
          className="mt-2 inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800"
        >
          <Plus className="w-4 h-4" /> Ajouter un équipement
        </button>
      </Bloc>

      <Bloc titre="Rôles dans une équipe"
        compte={config.rolesEquipe.length} aide="Ce qu'on inscrit à côté d'un nom sur une feuille de match.">
        <ListeTexte
          valeurs={config.rolesEquipe}
          onChange={(rolesEquipe) => modifier({ rolesEquipe })}
          placeholder="Capitaine, Gardien…"
        />
      </Bloc>

      <Bloc
        titre="Vitrine publique"
        aide="Montrer ou non la composition des équipes sur le site du club."
      >
        <label className="flex items-start gap-2 text-sm text-gray-700 mb-3">
          <input
            type="checkbox"
            checked={config.vitrine?.equipes ?? false}
            onChange={(e) =>
              modifier({ vitrine: { ...config.vitrine, equipes: e.target.checked } })
            }
            className="mt-0.5 rounded border-gray-300 text-primary-600"
          />
          <span>
            Afficher une carte « équipes » sur la page d'accueil
            <span className="block text-xs text-gray-500">
              Un parent y trouve l'équipe de son enfant avant de venir s'inscrire.
            </span>
          </span>
        </label>

        {config.vitrine?.equipes && (
          <div className="pl-6 space-y-3 border-l-2 border-gray-100">
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={config.vitrine?.nomsComplets ?? false}
                onChange={(e) =>
                  modifier({ vitrine: { ...config.vitrine, nomsComplets: e.target.checked } })
                }
                className="mt-0.5 rounded border-gray-300 text-primary-600"
              />
              <span>
                Afficher les noms de famille en entier
                <span className="block text-xs text-amber-700">
                  Sans cette case, les joueurs paraissent en « Lucas M. ». Une équipe de U11 est
                  une liste d'enfants mineurs : publier leurs noms complets sur internet demande
                  l'accord des familles.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={config.vitrine?.photos ?? false}
                onChange={(e) =>
                  modifier({
                    vitrine: {
                      ...config.vitrine,
                      photos: e.target.checked,
                      // Fermer les portraits ferme aussi celui des mineurs :
                      // laisser la seconde case cochée sous une première
                      // décochée est un piège pour le prochain qui l'ouvrira.
                      photosMineurs: e.target.checked ? config.vitrine?.photosMineurs : false,
                    },
                  })
                }
                className="mt-0.5 rounded border-gray-300 text-primary-600"
              />
              <span>
                Afficher les photos d'identité
                <span className="block text-xs text-gray-500">
                  Les majeurs seulement. Les mineurs restent sans portrait tant que la case
                  suivante n'est pas cochée.
                </span>
              </span>
            </label>

            {config.vitrine?.photos && (
              <label className="flex items-start gap-2 text-sm text-gray-700 pl-6">
                <input
                  type="checkbox"
                  checked={config.vitrine?.photosMineurs ?? false}
                  onChange={(e) =>
                    modifier({ vitrine: { ...config.vitrine, photosMineurs: e.target.checked } })
                  }
                  className="mt-0.5 rounded border-gray-300 text-primary-600"
                />
                <span>
                  Afficher aussi les photos des mineurs
                  <span className="block text-xs text-amber-700">
                    Publier le visage d'un enfant demande l'accord écrit de ses responsables
                    légaux, et se retire à leur simple demande. Un adhérent dont la date de
                    naissance manque est traité comme mineur.
                  </span>
                </span>
              </label>
            )}

            <input
              value={config.vitrine?.titre ?? ''}
              onChange={(e) => modifier({ vitrine: { ...config.vitrine, titre: e.target.value } })}
              placeholder="Titre de la page — « Nos équipes »"
              className="w-full px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
            />

            <textarea
              value={config.vitrine?.texte ?? ''}
              onChange={(e) => modifier({ vitrine: { ...config.vitrine, texte: e.target.value } })}
              placeholder="Phrase d'accroche, sous le titre (facultative)"
              rows={2}
              className="w-full px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
            />

            <p className="text-xs text-gray-500">
              Seules les équipes composées paraissent. Aucun téléphone, aucune adresse, aucun âge :
              la feuille de match reste au vestiaire.
            </p>
          </div>
        )}
      </Bloc>

      {/*
        Les frais bancaires, juste avant le paiement en plusieurs fois.

        Les deux règlent la même chose — ce que la famille paie et comment —
        et les séparer obligerait à traverser les catégories d'âge entre les
        deux.
      */}
      <Bloc
        titre="Frais bancaires"
        aide="Qui paie la commission du paiement en ligne : votre association, ou la famille."
      >
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={config.fraisStripe?.reportes ?? false}
            onChange={(e) =>
              modifier({ fraisStripe: { ...config.fraisStripe, reportes: e.target.checked } })
            }
            className="mt-0.5 rounded border-gray-300 text-primary-600"
          />
          <span>
            Ajouter les frais bancaires au montant demandé
            <span className="block text-xs text-gray-500">
              Pour que votre association encaisse exactement le prix que vous avez fixé.
            </span>
          </span>
        </label>

        {/*
          LE TEXTE INFORMATIF, ET POURQUOI IL EST LONG.

          Ce réglage change ce que les familles voient sur leur relevé. Un
          trésorier qui le coche sans avoir compris le découvre par un appel
          — « pourquoi 122,09 et pas 120 ? » — et le décoche en urgence au
          milieu des inscriptions, laissant deux tarifs différents dans la
          même saison.

          Trois phrases lues avant de cocher valent mieux qu'une explication
          au téléphone en septembre.
        */}
        <div className="mt-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 p-3 space-y-2 text-xs text-gray-600 dark:text-gray-400">
          <p>
            Chaque règlement par carte est amputé d'une commission de{' '}
            <strong>1,5 % + 0,25 €</strong> avant d'arriver sur votre compte. Une licence à 120 €
            vous en rapporte donc <strong>117,95 €</strong> — soit, sur cent-cinquante licenciés,{' '}
            <strong>plus de trois cents euros</strong> qui manquent au budget sans que rien ne
            l'indique nulle part.
          </p>
          <p>
            En cochant cette case, la famille règle{' '}
            <strong>{euros(majorer(12000))}</strong> pour une licence à 120 € : la commission
            apparaît sur son paiement en ligne sous une ligne nommée «&nbsp;Frais
            bancaires&nbsp;», et votre association reçoit bien ses 120 €.
          </p>
          <p>
            La cotisation reste enregistrée à 120 € dans votre registre : les frais ne comptent
            pas comme un règlement, et vos comptes ne s'en trouvent pas gonflés.
          </p>
          <p>
            Rien ne change pour les paiements encaissés à la main — chèque, espèces, virement —
            ni pour le prélèvement en plusieurs fois, dont la commission est différente.
          </p>
        </div>

        {(config.fraisStripe?.reportes ?? false) && (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs text-gray-700 dark:text-gray-300">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400">
                  <th className="font-medium py-1 pr-4">Votre prix</th>
                  <th className="font-medium py-1 pr-4">La famille paie</th>
                  <th className="font-medium py-1">Vous recevez</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {[5000, 12000, 25000].map((prix) => (
                  <tr key={prix} className="border-t border-gray-200 dark:border-gray-700">
                    <td className="py-1 pr-4">{euros(prix)}</td>
                    <td className="py-1 pr-4">{euros(majorer(prix))}</td>
                    <td className="py-1 font-medium">{euros(prix)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Bloc>

      <Bloc titre="Relances d'échéance" aide="En jours avant l'échéance d'une cotisation ou d'une pièce. 0 = le jour même.">
        <label className="flex items-center gap-2 text-sm text-gray-700 mb-3">
          <input
            type="checkbox"
            checked={config.relances.actives}
            onChange={(e) => modifier({ relances: { ...config.relances, actives: e.target.checked } })}
            className="rounded border-gray-300 text-primary-600"
          />
          Envoyer les relances
        </label>
        <ListeTexte
          valeurs={config.relances.jours.map(String)}
          onChange={(jours) =>
            modifier({
              relances: {
                ...config.relances,
                jours: jours.map((j) => Number(j)).filter((j) => Number.isFinite(j) && j >= 0),
              },
            })
          }
          placeholder="30"
        />
      </Bloc>

      <Bloc
        titre="Paiement en plusieurs fois"
        aide="Le prélèvement automatique des cotisations, et les étalements que vous acceptez."
      >
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={config.prelevement?.actif ?? false}
            onChange={(e) =>
              modifier({ prelevement: { ...config.prelevement, actif: e.target.checked } })
            }
            className="rounded border-gray-300 text-primary-600"
          />
          Proposer le paiement en plusieurs fois
        </label>
        <p className="mt-1.5 text-xs text-gray-500">
          Les familles signent une autorisation de prélèvement en ligne. Le règlement en une fois
          par carte reste toujours proposé.
        </p>

        {config.prelevement?.actif && (
          <div className="mt-4 pl-6 border-l-2 border-gray-100 space-y-4">
            <div>
              <p className="text-sm text-gray-700 mb-2">Étalements acceptés</p>
              <div className="space-y-2">
                {ETALEMENTS.map((e) => (
                  <label key={e.nombre} className="flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={(config.prelevement?.echeances ?? []).includes(e.nombre)}
                      onChange={(evt) => {
                        const actuels = config.prelevement?.echeances ?? [1];
                        modifier({
                          prelevement: {
                            ...config.prelevement,
                            echeances: evt.target.checked
                              ? [...actuels, e.nombre].sort((a, b) => a - b)
                              : actuels.filter((n) => n !== e.nombre),
                          },
                        });
                      }}
                      className="mt-0.5 rounded border-gray-300 text-primary-600"
                    />
                    <span>
                      {e.libelle}
                      <span className="block text-xs text-gray-500">{e.aide}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-1">Jour du prélèvement</label>
              <input
                type="number"
                min={1}
                max={28}
                value={config.prelevement?.jour ?? 5}
                onChange={(e) =>
                  modifier({
                    prelevement: {
                      ...config.prelevement,
                      jour: Math.max(1, Math.min(28, Number(e.target.value) || 5)),
                    },
                  })
                }
                className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              {/* Au-delà du 28, février ferait sauter une échéance. */}
              <p className="mt-1 text-xs text-gray-500">
                Du 1 au 28. La première échéance part le jour de la signature ; les suivantes à
                cette date, mois après mois.
              </p>
            </div>

            <p className="text-xs text-gray-500">
              Un prélèvement met plusieurs jours à être confirmé par la banque, et peut être rejeté
              après coup — la cotisation n'est comptée réglée qu'au retour de celle-ci.
            </p>
          </div>
        )}
      </Bloc>

      <div className="border-t border-gray-100 pt-4 flex items-center gap-3">
        <button
          onClick={() => enregistrer.mutate(config)}
          disabled={enregistrer.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
        >
          {enregistrer.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          Enregistrer la configuration
        </button>
        <button
          onClick={() => setConfig(JSON.parse(JSON.stringify(query.data.configuration)))}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          Annuler les modifications
        </button>
      </div>
    </div>
  );
}
